// The browser client of the selected saved App's publish check (PUB's canonical CheckReport), for exactly one
// selection, on the frame's SESSION-BOUND fetch:
//   GET  /api/v1/company/artifacts/:artifactId/checks?versionId&revisionId&purpose  -> the current report
//   POST /api/v1/company/artifacts/:artifactId/checks/scan {artifactId,versionId,revisionId,purpose:'editor'}
//        -> a real static scan of the saved source, answering the new report
//   POST /api/v1/company/artifacts/:artifactId/checks/acknowledge {artifactId,versionId,revisionId,purpose:'editor',
//        reportRef,findingId,operationId} -> the owner's audited no-fix acknowledgment, answering the new report
//        (the SERVER resolves the check, the finding and its rights from those identifiers only)
// Reads and scans are ordinary retries (no operation id). The answer must parse exactly as a CheckReport or it
// is "unavailable"; nothing is guessed and no finding is invented.
import type { CheckFinding, CheckReport } from './findings-model';

export type CheckSelection = {
  artifactId: string;
  versionId: string;
  revisionId: string;
  purpose: 'viewer' | 'editor';
};

export class CheckReportUnavailable extends Error {
  constructor(readonly status: number | null) {
    super('check_report_unavailable');
    this.name = 'CheckReportUnavailable';
  }
}

const obj = (value: unknown): Record<string, unknown> | null =>
  value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
const str = (value: unknown) => (typeof value === 'string' && value.length > 0 ? value : null);
const SEVERITIES = new Set(['critical', 'high', 'moderate', 'low', 'unknown']);
const STATES = new Set(['checking', 'pass', 'blocked', 'expired', 'incomplete']);
const AUDIENCES = new Set(['owner', 'editor', 'member', 'viewer']);
const LABELS = new Set(['api_key', 'private_key', 'access_token', 'password']);

function parseFinding(value: unknown): CheckFinding | null {
  const raw = obj(value);
  const id = str(raw?.id);
  if (!raw || !id || !SEVERITIES.has(String(raw.severity)) || typeof raw.holds !== 'boolean')
    return null;
  const ack = raw.acknowledged;
  const ackObj = obj(ack);
  const acknowledged =
    ack === null
      ? null
      : ackObj && str(ackObj.by) && str(ackObj.at)
        ? { by: String(ackObj.by), at: String(ackObj.at) }
        : undefined;
  if (acknowledged === undefined) return null;
  const common = {
    id,
    severity: raw.severity as CheckFinding['severity'],
    holds: raw.holds,
    acknowledged,
  };
  if (raw.kind === 'secret') {
    const where = obj(raw.where);
    if (!where || (where.area !== 'code' && where.area !== 'built') || !str(where.file))
      return null;
    if (where.line !== null && typeof where.line !== 'number') return null;
    if (raw.label !== null && !LABELS.has(String(raw.label))) return null;
    return {
      ...common,
      kind: 'secret',
      label: raw.label as 'api_key' | null,
      where: { area: where.area, file: String(where.file), line: where.line as number | null },
    };
  }
  if (raw.kind === 'dependency') {
    if (!str(raw.package) || !str(raw.version)) return null;
    if (raw.fixAvailable !== null && typeof raw.fixAvailable !== 'boolean') return null;
    const url = raw.advisoryUrl;
    if (url !== null && !(typeof url === 'string' && url.startsWith('https://'))) return null;
    return {
      ...common,
      kind: 'dependency',
      package: String(raw.package),
      version: String(raw.version),
      fixAvailable: raw.fixAvailable as boolean | null,
      advisoryUrl: url as string | null,
    };
  }
  return null;
}

export function parseCheckReport(value: unknown): CheckReport | null {
  const raw = obj(value);
  if (
    !raw ||
    !STATES.has(String(raw.state)) ||
    !AUDIENCES.has(String(raw.audience)) ||
    typeof raw.canAcknowledge !== 'boolean' ||
    typeof raw.publicationEligible !== 'boolean' ||
    !Array.isArray(raw.findings) ||
    (raw.checkedAt !== null && typeof raw.checkedAt !== 'string') ||
    (raw.reportRef !== undefined && typeof raw.reportRef !== 'string')
  )
    return null;
  const findings: CheckFinding[] = [];
  for (const entry of raw.findings) {
    const finding = parseFinding(entry);
    if (!finding) return null;
    findings.push(finding);
  }
  return {
    state: raw.state as CheckReport['state'],
    audience: raw.audience as CheckReport['audience'],
    checkedAt: raw.checkedAt as string | null,
    findings,
    canAcknowledge: raw.canAcknowledge,
    publicationEligible: raw.publicationEligible,
    ...(typeof raw.reportRef === 'string' ? { reportRef: raw.reportRef } : {}),
  };
}

const path = (artifactId: string) =>
  `/api/v1/company/artifacts/${encodeURIComponent(artifactId)}/checks`;

export type FindingAcknowledgment = { findingId: string; reportRef: string };

export type CheckReportApi = {
  read(): Promise<CheckReport>;
  scan(): Promise<CheckReport>;
  /** A data write: the caller keeps ONE operation id per acknowledgment across an unknown outcome. */
  acknowledge(input: FindingAcknowledgment, operationId: string): Promise<CheckReport>;
};

/** `send` is the frame's SESSION-BOUND fetch (a replaced session ends every call through it). */
export function createCheckReportApi(
  selection: CheckSelection,
  send: typeof fetch,
): CheckReportApi {
  const { artifactId, versionId, revisionId, purpose } = selection;
  const answer = async (response: Response) => {
    if (response.status !== 200) throw new CheckReportUnavailable(response.status);
    const report = parseCheckReport(await response.json().catch(() => null));
    if (!report) throw new CheckReportUnavailable(null);
    return report;
  };
  return {
    async read() {
      const query = new URLSearchParams({ versionId, revisionId, purpose });
      return answer(await send(`${path(artifactId)}?${query}`, { credentials: 'include' }));
    },
    async scan() {
      return answer(
        await send(`${path(artifactId)}/scan`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ artifactId, versionId, revisionId, purpose: 'editor' }),
        }),
      );
    },
    async acknowledge({ findingId, reportRef }, operationId) {
      return answer(
        await send(`${path(artifactId)}/acknowledge`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({
            artifactId,
            versionId,
            revisionId,
            purpose: 'editor',
            reportRef,
            findingId,
            operationId,
          }),
        }),
      );
    },
  };
}
